/**
 * DataRoom - full real implementation per CHAP-95 (download) +
 * CHAP-96 (upload) + CHAP-97 (metadata edit).
 *
 * UX shape:
 *   - List of documents visible to the user (server-side sensitivity-
 *     filtered per the user's role + tier)
 *   - Per-row badges: sensitivity tier, provenance tier, category, size
 *   - Click row → fetch signed URL + browser-navigate to download
 *   - Admin/team see an Edit button per row + an Upload zone above the
 *     list
 *
 * Per CHAP-89 (Phase 1C #55 port placeholder replaced) + the Phase 1D
 * implementation tickets.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import {
  useDocuments,
  useDownloadDocument,
} from '../hooks/useDocuments.js';
import type {
  DataroomDocument,
  SensitivityTier,
  ProvenanceTier,
} from '../../shared/types.js';
import UploadZone from '../components/dataroom/UploadZone.js';
import EditDocumentModal from '../components/dataroom/EditDocumentModal.js';

const SENSITIVITY_BADGE: Record<
  SensitivityTier,
  { label: string; bg: string; fg: string }
> = {
  public: { label: 'Public', bg: 'bg-green-100', fg: 'text-green-800' },
  restricted: { label: 'Restricted', bg: 'bg-blue-100', fg: 'text-blue-800' },
  confidential: { label: 'Confidential', bg: 'bg-amber-100', fg: 'text-amber-900' },
  highly_confidential: { label: 'Highly confidential', bg: 'bg-red-100', fg: 'text-red-900' },
};

const PROVENANCE_BADGE: Record<
  ProvenanceTier,
  { label: string; bg: string; fg: string }
> = {
  authoritative: { label: 'Authoritative', bg: 'bg-green-50', fg: 'text-green-700' },
  working_draft: { label: 'Working draft', bg: 'bg-gray-100', fg: 'text-gray-700' },
  informal_note: { label: 'Informal note', bg: 'bg-gray-100', fg: 'text-gray-700' },
  scratch: { label: 'Scratch', bg: 'bg-gray-50', fg: 'text-gray-500' },
  superseded: { label: 'Superseded', bg: 'bg-orange-50', fg: 'text-orange-700' },
  archived: { label: 'Archived', bg: 'bg-gray-50', fg: 'text-gray-500' },
};

function formatBytes(n: number | null): string {
  if (!n) return 'n/a';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DataRoom() {
  const { slug } = useParams<{ slug: string }>();
  const tenantSlug = slug ?? '';
  const { data: tenantData } = useTenantData(tenantSlug);
  const { primaryRole, allowedRoles } = useUserRoles();
  const isAdminOrTeam =
    allowedRoles.includes('admin') || allowedRoles.includes('team');
  const isAdmin = allowedRoles.includes('admin');

  const { data, isLoading, error } = useDocuments(tenantSlug);
  const download = useDownloadDocument(tenantSlug);
  const [editing, setEditing] = useState<DataroomDocument | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  function handleDownload(docId: string, filename: string) {
    setDownloadError(null);
    download.mutate(docId, {
      onError: (err) => {
        setDownloadError(`Couldn't download "${filename}" - ${err.message}`);
      },
    });
  }

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Data Room</h2>
            <p className="text-neutral text-sm mt-1">
              Tenant-confidential documents for{' '}
              {tenantData?.tenant.displayName ?? 'this tenant'}.
            </p>
          </div>
          {isAdminOrTeam && (
            <button
              type="button"
              onClick={() => setShowUpload((v) => !v)}
              className="bg-navy text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-navy/90 flex-shrink-0"
              aria-pressed={showUpload}
            >
              {showUpload ? 'Close upload' : 'Upload'}
            </button>
          )}
        </div>
      </header>

      {showUpload && isAdminOrTeam && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-navy text-sm font-semibold mb-3">
            Upload documents (admin / team)
          </h3>
          <UploadZone slug={tenantSlug} />
        </section>
      )}

      {downloadError && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          {downloadError}
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Loading documents…
        </div>
      )}

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          Failed to load documents: {error.message}
        </div>
      )}

      {data && (
        <>
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {data.documents.length === 0 && (
                <li className="p-6 text-center text-neutral text-sm">
                  No documents visible to you yet.
                  {data.hiddenCount > 0 && (
                    <span>
                      {' '}
                      ({data.hiddenCount} hidden - request access from a Cabinet
                      admin)
                    </span>
                  )}
                </li>
              )}
              {data.documents.map((doc) => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  isAdminOrTeam={isAdminOrTeam}
                  onDownload={() => handleDownload(doc.id, doc.filename)}
                  onEdit={() => setEditing(doc)}
                  isDownloading={
                    download.isPending && download.variables === doc.id
                  }
                />
              ))}
            </ul>
          </section>

          {data.hiddenCount > 0 && (
            <p className="text-xs text-neutral italic px-2">
              {data.hiddenCount} additional document
              {data.hiddenCount === 1 ? '' : 's'} hidden - your tier (
              {primaryRole === 'customer' ? 'customer' : primaryRole}) doesn't
              have access. Talk to a Cabinet admin if you need elevated access.
            </p>
          )}
        </>
      )}

      {editing && (
        <EditDocumentModal
          slug={tenantSlug}
          doc={editing}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface DocRowProps {
  doc: DataroomDocument;
  isAdminOrTeam: boolean;
  onDownload: () => void;
  onEdit: () => void;
  isDownloading: boolean;
}

function DocRow({
  doc,
  isAdminOrTeam,
  onDownload,
  onEdit,
  isDownloading,
}: DocRowProps) {
  const sensitivity = SENSITIVITY_BADGE[doc.sensitivityTier];
  const provenance = PROVENANCE_BADGE[doc.provenanceTier];

  return (
    <li className="p-3 sm:p-4 hover:bg-gray-50">
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h4 className="text-navy text-sm font-semibold truncate">{doc.name}</h4>
            {doc.isPrimary && (
              <span
                title="Canonical version"
                className="text-xs text-green-700 font-medium flex-shrink-0"
              >
                ★ Primary
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1 min-w-0">
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${sensitivity.bg} ${sensitivity.fg}`}
            >
              {sensitivity.label}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded ${provenance.bg} ${provenance.fg}`}
            >
              {provenance.label}
            </span>
            {doc.category && (
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                {doc.category}
              </span>
            )}
            <span className="text-xs text-neutral">{formatBytes(doc.sizeBytes)}</span>
            {doc.fileType && (
              <span className="text-xs text-neutral uppercase">.{doc.fileType}</span>
            )}
          </div>
          {doc.description && (
            <p className="text-xs text-neutral mt-1 line-clamp-2">{doc.description}</p>
          )}
          {doc.sourceAttribution && (
            <p className="text-xs text-neutral mt-1 italic">
              source: {doc.sourceAttribution}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onDownload}
            disabled={isDownloading}
            className="bg-navy text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-navy/90 disabled:opacity-50"
          >
            {isDownloading ? '…' : 'Download'}
          </button>
          {isAdminOrTeam && (
            <button
              type="button"
              onClick={onEdit}
              className="text-xs font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
